<template>
  <div class="main-container">
    <div class="privacy-policy-modal">
      <label for="privacy-policy-close-button" class="iw-visuallyhidden">Close</label>
      <button id="privacy-policy-close-button" class="privacy-policy-modal__close-button" @click="hideModal">
        <svg
          class="privacy-policy-modal__image svg-inline--fa"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
        >
          <path
            fill="currentColor"
            stroke="currentColor"
            stroke-linecap="square"
            stroke-width="2"
            d="M1.368 1.368l13.264 13.264m0-13.264L1.368 14.632"
          />
        </svg>
      </button>
      <div class="privacy-policy-modal__text">
        <h2>Appetize Privacy Policy</h2>
        <h3>(Rev. August 1, 2020)</h3>
        <p>
          Appetize Technologies, Inc. (
          <strong class="privacy-policy-modal__strong">“Appetize,” “we”, “us” or “our”</strong>) operates its websites for the
          benefit of Appetize’s customers, business partners and end-users of our services (
          <strong class="privacy-policy-modal__strong">“customers,” “end-users” or “you”</strong>). This Privacy Policy describes
          our practices in connection with information that we collect when you visit any of our websites or use our other
          technologies or services, such as our contactl ordering services (collective, our “
          <strong class="privacy-policy-modal__strong">Services”</strong>).
        </p>

        <h2 class="privacy-policy-modal__header">General</h2>
        <p>
          Appetize values your privacy. As an Appetize customer, business partner or end-user, be assured that your privacy is
          protected. Information obtained directly from you is only used for the purposes stated in this Policy. Information that
          Appetize collects and maintains will not be released to any third party outside of Appetize except as otherwise
          disclosed in this Policy.
        </p>
        <p>
          You may find a copy of this Privacy Policy in PDF format
          <a
            id="privacy-policy-pdf"
            aria-label="download privacy policy in PDF format"
            href="https://3ovw5n2ev5na3kesko1d7lee-wpengine.netdna-ssl.com/wp-content/uploads/2020/09/VS2_P-Appetize-Privacy-Policy-.pdf"
            >here</a
          >.
        </p>

        <h2 class="privacy-policy-modal__header">
          Obtaining and Handling Your Consent
        </h2>
        <p>
          By using our Services or submitting Personal Information (as defined below) to us, you agree that we may collect your
          Personal Information and you consent to the use, disclosure and transfer of your Personal Information in accordance with
          this Policy and as permitted or required by law. If you do not agree with this Policy, please do not use our Services,
          provide us with your Personal Information or otherwise grant your consent for the collection and use of your Personal
          Information.
        </p>
        <p>
          You may refuse consent to our collection, use and disclosure of your Personal Information, subject to legal and
          contractual requirements and reasonable notice. You may withdraw or revoke your consent to further collection, use and
          disclosure of your Personal Information for certain of the purposes identified herein.
        </p>
        <p>
          There are instances where we may collect, use or disclose your Personal Information without your consent. For example,
          we may use or disclose your Personal Information to comply with a subpoena or court order or under applicable law.
        </p>

        <h2 class="privacy-policy-modal__header">
          What Information Appetize Collects
        </h2>
        <p>
          Appetize may capture one or more of the following categories of information about our customers, business partners
          and/or end-users of our Services (collectively, “Personal Information”) when you, among other things: (i) contact or
          interact with us by email, text or telephone, (ii) engage with us on our website or social media, (iii) register with us
          or sign up for demos, webinars or other content or services we provide through our website; (iv) if we meet you in
          person at industry conferences and events and you register with us or request information from us at that time; or (v)
          use our Services:
        </p>

        <table class="privacy-policy-modal__table">
          <tr>
            <th>Categories of Personal Information</th>
            <th>Specific Personal Information</th>
            <th>Source of Personal Information</th>
          </tr>
          <tr>
            <td>Personal Identifiers</td>
            <td>
              Name
              <br />Address <br />Phone and fax <br />Email address <br />Mobile phone
              <br />
            </td>
            <td>
              Appetize account, registration or order page, email, social media, text, telephone or in person at industry events/
              conferences (personal information collected directly from user)
            </td>
          </tr>
          <tr>
            <td>Professional or employment related information</td>
            <td>
              Company name
              <br />Company information <br />Job Title/Role
              <br />
            </td>
            <td>
              Appetize registration page email, social media, text, telephone or in person at industry events/ conferences
              (information collected directly from user)
            </td>
          </tr>
          <tr>
            <td>Internet/Electronic Activity</td>
            <td>
              User status when logged into your Appetize account; IP address; device and browser type; limited browsing history;
              navigation through our site; information regarding your interaction with email messages from us (such as whether you
              opened them), MAC Address
            </td>
            <td>
              Server logs, website cookies and clear GIFs (information automatically collected from users using website
              technologies)
            </td>
          </tr>
          <tr>
            <td>Commercial Purchasing Information</td>
            <td>
              Products/goods purchased or obtained Purchase/ consuming history
            </td>
            <td>Appetize contactless menu ordering</td>
          </tr>
          <tr>
            <td>Geolocation</td>
            <td>Location of mobile device</td>
            <td>
              Appetize contactless menu ordering, use of QR codes at kiosks
            </td>
          </tr>
          <tr>
            <td>Financial Information</td>
            <td>Banking/credit/debit card account numbers</td>
            <td>Appetize contactless menu ordering</td>
          </tr>
        </table>

        <h2 class="privacy-policy-modal__header">
          How Appetize Uses the Information it Collects
        </h2>
        <p>
          This Privacy Policy describes how Appetize treats your Personal Information.
        </p>

        <h2 class="privacy-policy-modal__header">
          How Do We Use Your Information?
        </h2>
        <p>
          We may use the Personal Information we collect from you for reasons related to our business operations and as set forth
          in this Policy. Examples of ways that we may use such Personal Information include the following:
        </p>
        <ul>
          <li>Responding to your requests;</li>
          <li>Enabling mobile contactless menu ordering;</li>
          <li>
            Sending SMS messages to you when you use the Appetize contactless menu ordering service or our other Services, if you
            have consented to receive SMS messages on your mobile device;
          </li>
          <li>
            Providing you with demos related to our products and Services;
          </li>
          <li>
            Registering you for and providing webinars related to our products and Services;
          </li>
          <li>
            Communicating with you and marketing to you regarding our products and Services;
          </li>
          <li>
            Contacting you at a telephone number, email address, social media account/profile or any other means you have given us
            to provide you with information about our products and Services;
          </li>
          <li>Providing apps or interactive features of our website;</li>
          <li>Tracking our visitors’ use of our website;</li>
          <li>Improving the content, appearance and utility of our website;</li>
          <li>
            Compliance, audits and providing assistance to law enforcement; and
          </li>
          <li>
            To complete a corporate transaction, such as a merger, sale, joint venture or disposition of all or any portion of our
            business, assets or capital stock.
          </li>
        </ul>

        <h2 class="privacy-policy-modal__header">
          How Do We Share Your Personal Information?
        </h2>
        <p>
          Appetize is committed to protecting your Personal Information. The data you provide to us or enter into the Appetize
          website is considered your confidential and proprietary information.
          <strong class="privacy-policy-modal__strong">
            <i>
              It is our policy to respect the privacy of our customers, and for this reason we do not sell our customers’ Personal
              Information.
            </i>
          </strong>
          We do, however, share your Personal Information with our customers and business partners that provide you with in-person
          goods and services, such as those for mobile contactless menu ordering. We may also share your Personal Information with
          our own third-party service providers solely for our own business purposes, or if required to do so by law or in the
          good faith belief that such disclosure is necessary to:
        </p>
        <ol type="1">
          <li>
            conform to the law or comply with legal process served on Appetize;
          </li>
          <li>protect and defend the rights or property of Appetize; or</li>
          <li>
            act under exigent circumstances to protect the personal safety of our customers or the public.
          </li>
        </ol>
        <h2 class="privacy-policy-modal__header">
          Do We Monitor Who Uses Our Website?
        </h2>
        <p>
          Each time a user visits our website, our server collects certain use and navigation information. This information is
          aggregated and analyzed to tell us when and how the website is used and navigated, including the number, frequency and
          duration of visits to each web page. It also helps to inform us of any performance-related issues we have so that we can
          correct them to provide you with a more efficient website.
        </p>

        <h2 class="privacy-policy-modal__header">
          What are "Cookies" and How Do We Use Them?
        </h2>
        <p>
          Cookies are small files that are stored by your browser on your computer's hard drive while accessing the Internet.
          Server programs that provide information back to the website then read these files. Most companies like ours make use of
          browser cookies. When you access our Appetize website, we may use cookies occasionally to store some information on your
          computer or mobile device. On occasion, we will refresh the cookies as those parts of the website are modified or
          enhanced. Our cookies do not contain any Personal Information about you. The cookies will not enable third parties to
          access any of the information stored on your computer. Most web browsers are set to automatically accept cookies, but
          you can usually either change the browser preferences to warn when a cookie is about to be written, or to not accept
          them at all. Parts of our website will function without cookies, but cookies may be required to use certain parts of our
          website or services.
        </p>

        <h2 class="privacy-policy-modal__header">Automated Decision Making</h2>
        <p>
          We do not use automated decision making to display or send recommendations and personalized offers to you based on your
          Personal Information.
        </p>

        <h2 class="privacy-policy-modal__header">
          Retention of Your Personal Information
        </h2>
        <p>
          Personal Information that you provide to us will be retained by us for so long as necessary for the purpose intended of
          that Personal Information or in accordance with applicable laws./p>
        </p>

        <h2 class="privacy-policy-modal__header">Security</h2>
        <p>
          We seek to use reasonable organizational, technical and administrative measures to protect Personal Information within
          our organization. Unfortunately, no data transmission or storage system can be guaranteed to be 100% secure. If you have
          reason to believe that your interaction with us is no longer secure (for example, if you feel that the security of your
          account has been compromised), please immediately notify us in accordance with the "Contact Us" section below.
        </p>

        <h2 class="privacy-policy-modal__header">Do We Market to Children?</h2>
        <p>
          Children’s safety is important to us, and we encourage parents and guardians to take an active interest in the online
          activities of their children. Our services are not directed to children, and we do not knowingly collect Personal
          Information from children, including those under the age of 13, without obtaining parental consent. If we learn that we
          have collected Personal Information from a child under the age of 13, we will delete that information as quickly as
          possible. If you believe that we may have collected any such personal information, please notify us at
          <a href="mailto: support@appetize.com">support@appetize.com</a>.
        </p>

        <h2 class="privacy-policy-modal__header">
          Cross-Border Data Transfer of Your Personal Information.
        </h2>
        <p>
          Your Personal Information may be transferred across national borders and may be accessed by us and our affiliated
          vendors and suppliers from countries with varying degrees of protection for your Personal Information. Any international
          transfer of your Personal Information will be done in accordance with all applicable privacy and data protection laws
          and regulations. While in another jurisdiction for processing, your Personal Information may be accessed by the courts,
          law enforcement and national security authorities of that jurisdiction. Our servers and data centers are located in the
          United States. If you choose to use the Services from outside the U.S., then you should know that you are transferring
          your Personal Information outside of your region and into the U.S. for storage and processing. By providing your
          Personal Information to us through your use of our services, you agree to that transfer, storage and processing in the
          U.S.
        </p>

        <h2 class="privacy-policy-modal__header">Choice and Opt-Out</h2>
        <p>
          Subject to reasonable notice, where we have relied on your consent for the collection or processing of your Personal
          Information, you may withdraw your consent to certain of the purposes identified herein at any time, unless your
          Personal Information is necessary for us to fulfill our legal requirements or similar obligations. To withdraw your
          consent, please contact us at the below address and advise us of what Personal Information you no longer wish us to use.
        </p>
        <p>
          If you withdraw or revoke your consent, you acknowledge that we may not be able to provide you or continue to provide
          you with certain services or information which may be of value to you. In the event of changes or opt-out requests, we
          may retain your Personal Information for reasonable purposes, for example in connection with your past transactions.
        </p>
        <p>
          Please note that we may need to identify you and ask you for additional information to fulfill your opt-out request and
          that applicable law may contain restrictions and other provisions which relate to the opt-out rights we described above.
        </p>

        <h2 class="privacy-policy-modal__header">Marketing and Promotions</h2>
        <p>
          If, at any time, you no longer wish to receive marketing or promotional materials or communications from us, you may
          send an email to
          <a href="mailto: marketing@appetize.com">marketing@appetize.com.</a>
          In addition, each time we send you an e-mail marketing message, we give you the option to unsubscribe from the e-mail
          list by simply clicking the “unsubscribe” link included in the e-mail. If we send you text message marketing
          communications, you may opt out of receiving such messages in the future by replying STOP to the text message, or as
          otherwise set forth in the text message.
        </p>

        <h2 class="privacy-policy-modal__header">
          California Privacy Rights and Choices
        </h2>
        <p>
          The California Consumer Privacy Act of 2018 (the “CCPA”) provides California residents with specific rights regarding
          the collection and storage of their personal information. This section describes how these rights apply to you
          <strong class="privacy-policy-modal__strong">
            <i>if you are a California resident.</i>
          </strong>
        </p>

        <h2 class="privacy-policy-modal__header">
          Access to Specific Information and Data Portability Rights
        </h2>
        <p>
          You have the right to request that Appetize disclose the following information to you about our collection and use of
          your Personal Information over the past twelve (12) months. Once we have received a request from you, we may ask you to
          provide certain information to identify you and verify your request. Upon verification, we will disclose to you:
        </p>
        <ol type="1">
          <li>
            The categories of Personal Information we collected about you.
          </li>
          <li>
            The categories of sources for the Personal Information we collected about you (e.g., use of cookies).
          </li>
          <li>
            The specific pieces of Personal Information we collected about you.
          </li>
          <li>
            Our business or commercial purpose for collecting that Personal Information.
          </li>
          <li>
            <strong class="privacy-policy-modal__strong">We do not sell Personal Information</strong>, however, if we disclose
            your Personal Information to a third-party, we will provide to you the categories of third parties with whom we share
            Personal Information and the categories of information that we share with each third-party recipient.
          </li>
        </ol>

        <h2 class="privacy-policy-modal__header">Deletion Request Rights</h2>
        <p>
          You have the right to request that Appetize delete any of the Personal Information collected from you and retained by
          us, subject to certain exceptions. Once your request is verified and we have determined that we are required to delete
          the requested Personal Information in accordance with the CCPA, we will delete, and direct our third-party service
          providers to delete, your Personal Information from our records. Your request to delete Personal Information that we
          have collected may be denied if we conclude it is necessary for us to retain such Personal Information under one or more
          of the exceptions listed in the CCPA.
        </p>

        <h2 class="privacy-policy-modal__header">Exercising Your Rights</h2>
        <p>
          To exercise any of the rights described above, please submit a verifiable consumer request to us by either:
        </p>
        <ul>
          <li>Calling us at 1.877.559.4225</li>
          <li>
            Visiting
            <a href="https:\\www.appetize.com">www.appetize.com</a>
          </li>
          <li>
            Sending an email to
            <a href="mailto:privacy@appetize.com">privacy@appetize.com</a>
          </li>
        </ul>
        <p>
          Only you, or a person registered with the California Secretary of State that you authorize to act on your behalf, may
          make a verifiable consumer request related to your Personal Information. You may also make a verifiable consumer request
          on behalf of your minor child. You may only make a verifiable consumer request to delete Personal Information twice
          within a 12-month period. Your verifiable consumer request must:
        </p>
        <ul>
          <li>
            Provide sufficient information to allow us to reasonably verify that you are the person about whom we collected
            Personal Information, including proof of your California residency, or a person authorized to act on your behalf; and
          </li>
          <li>
            Describe your request with sufficient detail to properly understand and respond to it.
          </li>
        </ul>
        <p>
          We will only use the Personal Information that you have provided in a verifiable consumer request in order to verify
          your request. We cannot respond to your request or provide you with Personal Information if we cannot verify your
          identity or authority.
        </p>
        <p>
          Making a verifiable consumer request does not require you to create an account with us. We consider a request made
          through your password-protected account sufficiently verified when the request relates to Personal Information
          associated with that specific account.
        </p>

        <h2 class="privacy-policy-modal__header">Response Timing and Format</h2>
        <p>
          Our goal is to respond to verifiable customer requests within forty-five (45) days of receipt. If we are unable to
          deliver a response to verifiable consumer requests within this timeframe, we will inform you of the reason and estimated
          extension period in writing. We will deliver a response to your existing account with us, if applicable, or a written
          response by mail or electronically, at your option.
        </p>
        <p>
          Any disclosures will cover only the 12-month period preceding the date on which we receive your verifiable consumer
          request. The response we provide will also explain the reasons we cannot comply with a request, if applicable. Data
          portability requests will be issued in a format that is readily usable. We do not charge a fee for responding to
          verifiable consumer requests unless your request is excessive, repetitive or manifestly unfounded. If the request
          warrants a reasonable fee, we will tell you why and provide you with a cost estimate before completing your request.
        </p>

        <h2 class="privacy-policy-modal__header">
          Changes and Updates to this Policy
        </h2>
        <p>
          From time to time, we may make changes or updates to our Privacy Policy. As we do, we will make them available on our
          website so be sure to check occasionally for any changes. Your continued use of our Services will constitute your
          acceptance of and agreement to any such changes.
        </p>

        <h2 class="privacy-policy-modal__header">
          What if I Have Questions or Comments?
        </h2>
        <p>
          If you have any questions or comments regarding our Privacy Policy, feel free to contact us at
          <a href="mailto:privacy@appetize.com">privacy@appetize.com</a>.
        </p>
        <p>
          If you wish to access or change any of the Personal Information collected by Appetize, you may do so by sending an email
          to
          <a href="mailto:privacy@appetize.com">privacy@appetize.com</a>. When contacting us, please be sure to provide us with
          your name, exact e-mail address, physical address and/or telephone number(s) so that we may handle your request
          appropriately. In some circumstances, we may not provide access to your Personal Information if, for example, it
          contains the Personal Information of other people, if it constitutes confidential commercial information, or if it is
          otherwise not the subject of an access request.
        </p>
        <p>
          The parties have agreed that this Policy and any of its accessories be written in the English language. Les parties aux
          présentes ont exigé que ce contrat et ses accessoires, y compris tout avis, soient rédigés en anglais. Both the English
          and French versions of this Policy have equal legal value.
        </p>
        <p>
          © Copyright 2020 Appetize Technologies, Inc. Appetize and the Appetize logo are registered trademarks of Appetize
          Technologies, Inc. All rights reserved.
        </p>
        <p>Last updated: As of August 1, 2020</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicyModal",
  methods: {
    hideModal() {
      this.$modal.hide("PrivacyPolicyModal");
    }
  }
};
</script>
<style lang="scss" scoped>
.dark {
  .privacy-policy-modal {
    color: $primary-color;
  }
}

.main-container {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.privacy-policy-modal {
  display: flex;
  justify-content: left;
  align-items: flex-start;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
  height: 100%;
  font-family: $theme2__fontFamilyPrimary;
  color: $text-color;

  &__text {
    display: block;
    width: 100%;
    font-size: 20px;
    font-weight: 100;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.65;
    letter-spacing: normal;
    margin-top: 85px;
    margin-bottom: 85px;
    margin-left: 85px;
    margin-right: 85px;

    @include respond-to-mobile {
      margin-left: 27px;
      margin-right: 27px;
      font-size: 14px;
      font-weight: normal;
      font-stretch: normal;
      font-style: normal;
      line-height: 1.5;
      letter-spacing: normal;
    }
  }

  &__close-button {
    all: unset;
    cursor: pointer;
    position: absolute;
    top: 20px;
    right: 20px;

    @include respond-to-mobile {
      top: 7px;
      right: 7px;
    }

    img {
      width: 20px;
      height: 20px;
    }
  }

  &__close-button:focus {
    outline: orange 5px auto;
  }

  &__header {
    margin-top: 50px;
  }
  &__table {
    width: 100%;
    table-layout: fixed;
    border: 1px solid;
    border-color: $text-color;
    border-collapse: collapse;
    margin-bottom: 85px;
  }
  &__strong {
    font-weight: bold;
  }
}
th {
  border: 1px solid;
  border-color: $text-color;
  vertical-align: top;
  font-size: 14px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 15px;
  padding-right: 15px;
}

td {
  border: 1px solid;
  border-color: $text-color;
  vertical-align: top;
  font-size: 12px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 15px;
  padding-right: 15px;
}

li {
  margin-top: 15px;
  padding-left: 20px;
}

a {
  opacity: 0.7;
  &:hover {
    text-decoration: underline;
  }
}
</style>
